
import React, { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Heart, MessageCircle, Clock, Share2 } from "lucide-react";
import { motion } from "framer-motion";
import { formatDistanceToNow } from "date-fns";

interface UserProfile {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
}

interface PostMedia {
  id: string;
  media_url: string;
  media_type: string;
}

interface Post {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  likes_count: number;
  comments_count: number;
  has_liked?: boolean;
  media?: PostMedia[];
}

interface UserProfilePostProps {
  post: Post;
  profile: UserProfile;
  onLike: () => void;
  onComment: (comment: string) => void;
}

const UserProfilePost: React.FC<UserProfilePostProps> = ({ post, profile, onLike, onComment }) => {
  const [comment, setComment] = useState("");
  const [showCommentInput, setShowCommentInput] = useState(false);
  
  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (comment.trim()) {
      onComment(comment.trim());
      setComment("");
      setShowCommentInput(false);
    }
  };
  
  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name.split(' ').map(part => part[0]).join('').toUpperCase();
  };
  
  const formattedDate = post.created_at ? 
    formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) : 
    "recently";

  return (
    <Card className="overflow-hidden border shadow-sm">
      <CardHeader className="bg-muted/30 py-3 px-4">
        <div className="flex items-center gap-2">
          <Avatar className="h-10 w-10">
            <AvatarImage src={profile?.avatar_url || undefined} />
            <AvatarFallback>{getInitials(profile?.full_name)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm">{profile?.full_name || 'Anonymous'}</p>
            <div className="flex items-center text-xs text-muted-foreground">
              <Clock className="h-3 w-3 mr-1" />
              <span>{formattedDate}</span>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-3 px-4">
        <p className="text-sm whitespace-pre-wrap">{post.content}</p>
        
        {post.media && post.media.length > 0 && (
          <div className="mt-3 grid grid-cols-1 gap-2">
            {post.media.map((media, index) => (
              <div key={media.id} className="rounded-md overflow-hidden">
                {media.media_type.includes('image') ? (
                  <img 
                    src={media.media_url} 
                    alt="Post image" 
                    className="w-full h-auto max-h-[300px] object-cover"
                  />
                ) : media.media_type.includes('video') ? (
                  <video 
                    src={media.media_url} 
                    controls
                    className="w-full h-auto max-h-[300px] object-cover"
                  />
                ) : null}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      
      <CardFooter className="flex flex-col pt-0 px-4 pb-3">
        <div className="flex items-center justify-between w-full pt-2">
          <div className="flex items-center gap-1.5">
            <Button 
              size="sm" 
              variant="ghost" 
              className="flex items-center gap-1 h-8 px-2"
              onClick={onLike}
            >
              <motion.div
                whileTap={{ scale: 1.2 }}
                transition={{ duration: 0.1 }}
              >
                <Heart 
                  className={`h-4 w-4 ${post.has_liked ? 'text-red-500 fill-red-500' : ''}`} 
                />
              </motion.div>
              <span className="text-xs">{post.likes_count}</span>
            </Button>
            
            <Button 
              size="sm" 
              variant="ghost" 
              className="flex items-center gap-1 h-8 px-2"
              onClick={() => setShowCommentInput(!showCommentInput)}
            >
              <MessageCircle className="h-4 w-4" />
              <span className="text-xs">{post.comments_count}</span>
            </Button>
            
            <Button 
              size="sm" 
              variant="ghost" 
              className="flex items-center gap-1 h-8 px-2"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {showCommentInput && (
          <form onSubmit={handleSubmitComment} className="flex gap-2 mt-3 w-full">
            <Input
              className="text-sm"
              placeholder="Add a comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <Button 
              type="submit" 
              size="sm"
              disabled={!comment.trim()}
            >
              Post
            </Button>
          </form>
        )}
      </CardFooter>
    </Card>
  );
};

export default UserProfilePost;
